import React, { FC, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  TableContainer,
  Chip,
  Button,
  IconButton,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  MenuList
} from '@mui/material';
import LogoSpinner from 'src/components/LogoSpinner';
import { ActionCell, DynamicTable } from '../DynamicTable/DynamicTable';
import { ZoomIn } from '@mui/icons-material';
import { Variant, PrizeProducts } from '../../interface/interface';
import { IServerFilter, ServerFilter } from '../ServerFilter/ServerFilter';
import { toDateTime } from '../../utils/payloadFormatter';
import { useProductAPI, PaginatedProducts } from 'src/hooks/useProductAPI';
import { useOrderDataStore } from '../../zustandStore/store.order-data';
import { useOrderManagementStore } from '../../zustandStore/store.ordermgt';
import NumberInputIntroduction from '../shared/CustomNumberInput';
import { API } from 'src/config/config';

interface IVariantSelector {
  className?: string;
  onVariantSelect?: (variant: any) => void;
}

type OrderCreateRequest = {
  variantId: string;
  quantity: number;
  notes?: string;
  amountPaid?: number;
  addressId?: string;
};

type PageData = {
  content: any[];
  pageable: Pageable;
  totalPages: number;
  totalElements: number;
  last: boolean;
  size: number;
  number: number;
  sort: any;
  first: boolean;
  numberOfElements: number;
};

export interface Pageable {
  pageNumber: number;
  pageSize: number;
  sort: Sort;
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface Sort {
  empty: boolean;
  sorted: boolean;
  unsorted: boolean;
}

type SelectedVariantType = {
  id: string;
  name: string;
  price: number;
  quantity: number;
};

export const ProductSelector: React.FC<IVariantSelector> = (
  props: IVariantSelector
) => {
  const { onVariantSelect } = props;
  const orderDataStore = useOrderDataStore((state) => state);
  const orderManagementState = useOrderManagementStore((state) => state);
  const { searchProducts, searchLoading, error: apiError } = useProductAPI();
  const [productVariants, setProductVariants] = useState<PrizeProducts[]>([]);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [pageData, setPageData] = useState<PaginatedProducts>(null);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [isInSearchMode, setIsInSearchMode] = useState(false);
  const [filterApplied, setFilterApplied] = useState<IServerFilter>(null);
  const [reload, setReload] = useState(false);
  const [loading,setLoading] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<Map<String, any>>(
    new Map()
  );

  const handleInputChange = useCallback(
    (value) => {
      if (!selectedVariant.has(value.row.id)) {
        const variant = {
          id: value.row.id,
          vendor: value.row.vendor,
          title: value.row.productName,
          status: value.row.status,
          updatedAt: value.row.updatedAt,
          notes: '',
          quantity: value.row.quantity
        };
        setSelectedVariant(new Map(selectedVariant.set(value.row.id, variant)));
        onVariantSelect(selectedVariant.get(value.row.id)); // Highlight
        // Add to the store
        orderDataStore.actions.addOrder({
          variantId: value.row.id,
          quantity: value.row.quantity || 1,
          notes: value.row.notes || '',
          amountPaid: value.row.salePrice || 0,
          addressId: orderDataStore.settings?.address?.id,
          productName: value.row.productName
        });
      } else {
        // If already selected, remove it from the selectedVariant map
        const newSelectedVariant = new Map(selectedVariant);
        newSelectedVariant.delete(value.row.id);
        setSelectedVariant(newSelectedVariant);
        // onVariantSelect(selectedVariant.size); // Highlight
        // Remove from the store
        orderDataStore.actions.removeOrder(
          value.row.id,
          orderDataStore.settings?.address?.id
        );
      }
    },
    [selectedVariant]
  );

  const numberInputChange = useCallback(
    (value, _v) => {
      const id = value.row.id;
      const quantity = parseInt(_v);
      setSelectedVariant(
        new Map(
          selectedVariant.set(id, {
            ...value.row,
            quantity
          })
        )
      );
      onVariantSelect({ ...value.row, quantity });
      // Update the store
      orderDataStore.actions.addOrder({
        variantId: id,
        quantity: quantity,
        notes: value.row.notes || '',
        amountPaid: value.row.salePrice || 0,
        addressId: orderDataStore.settings?.address?.id
      });
    },
    [selectedVariant, orderDataStore]
  );

  useEffect(() => {
    // Get the selected variant from the store
    const selectedVariants = orderDataStore.settings.orders;
    if (selectedVariants && selectedVariants.length > 0) {
      const variantMap = new Map<string, any>();
      selectedVariants.forEach((variant: OrderCreateRequest) => {
        variantMap.set(variant.variantId, {
          ...variant,
          quantity: variant.quantity || 1 // Ensure quantity is set
        });
      });
      setSelectedVariant(variantMap);
      // Update the count of selected variants
    }
  }, []);
  // Create the type for the extension
  //

  // One time Subscription
  useEffect(() => {
    const fetchVariants = async () => {
      try {
        const result = await searchProducts({
          query: currentSearchQuery,
          filter: filterApplied,
          page,
          size: rowsPerPage
        });
        if (result && Array.isArray(result.content)) {
          setProductVariants(result.content);
          setPageData(result);
        }
      } catch (error) {
        console.error('Error fetching variants:', error);
      }
    };

    fetchVariants().then();
  }, []);

  return (
    <Card>
      <ServerFilter
        filtersAction={setFilterApplied}
        endpoint={`${API.HOST}/api/v1/products/search`}
        onQueryChange={(query) => {
          setCurrentSearchQuery(query);
        }}
        hideProductName={true}
        setLoader={(state) => {
          setLoading(state);
        }} // Hook manages loading state internally
        onResultFetched={async (data) => {

          if (data) {
            setProductVariants(data.content);
            setPageData(data);
            setPage(data.number || 0);
          }
        }}
        products={productVariants?.map((item) => ({
          id: item.id,
          title: item.productName
        }))}
      />

      <TableContainer>
        {loading ? (
          <Box
            sx={{
              height: '400px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <LogoSpinner />
          </Box>
        ) : (
          <DynamicTable
            showPagination={true}
            rowsPerPage={isInSearchMode ? pageData?.totalElements : rowsPerPage}
            page={page}
            totalPage={pageData?.totalElements}
            sortByField={sortBy}
            onSort={(asc: 'desc' | 'asc') => {
              setSortBy(asc);
              setSortAsc(asc);
            }}
            sort={sortAsc}
            headers={[
              {
                headerName: 'Select',
                field: 'select',
                renderCell: (value) => {
                  console.log(
                    'is selected?',
                    selectedVariant.has(value.row?.id)
                  );
                  return (
                    <Checkbox
                      onChange={(e) => handleInputChange(value)}
                      checked={selectedVariant.has(value.row?.id)}
                    />
                  );
                }
              },
              {
                headerName: 'Product Name',
                field: 'productName'
              },
              // {
              //   headerName: 'Colors',
              //   field: 'colors',
              //   renderCell: (value) => {
              //     return (
              //       <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              //         {value?.row.actions.variantAttributeValues
              //           ?.filter((item) => item.name === 'Colors')
              //           .map((colorItem, index) => (
              //             <Chip
              //               key={index}
              //               label={colorItem.productAttributeValue}
              //               sx={{
              //                 margin: '2px',
              //                 backgroundColor: 'gold',
              //                 color: 'black',
              //                 textTransform: 'capitalize'
              //               }}
              //             />
              //           ))}
              //       </Box>
              //     );
              //   }
              // },
              {
                headerName: 'Quantity',
                field: 'quantity',
                renderCell: (value) => {
                  return (
                    <>
                      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                        <NumberInputIntroduction
                          min={1}
                          max={30}
                          onChange={(e: any, v: any) =>
                            numberInputChange(value, v)
                          }
                          value={
                            selectedVariant.get(value.row.id)?.quantity || 1
                          }
                        />
                      </FormControl>
                    </>
                  );
                }
              }
            ]}
            rows={productVariants?.map((item) => ({
              id: item.id,
              productName: item.productName,
              status: item.status,
              updatedAt: toDateTime(item.updatedAt),
              actions: item,
              quantity: 0,
              notes: '',
              salePrice: item.salePrice
            }))}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(event) => setRowsPerPage(event)}
          />
        )}
      </TableContainer>
    </Card>
  );
};
